import { useCallback, useRef } from "react";
import { FieldMappingCreationAndEditDict } from "../../../../../../models/Entities";
import { REDACTED_VALUE_TEXT } from "../../../../../../constants";
import { stringifyRemoteFieldValue } from "../../../../../shared/utils/remoteFieldValue";

export const MAX_INDEXED_VALUE_LENGTH = 256;

const ROOT_OBJECT_TRAVERSAL_PATH = "ROOT_OBJECT";

type IndexedRemoteField = {
  displayName: string;
  commonModelName: string;
  sampleValue: string;
};

export type RemoteFieldSearchIndex = Map<FieldMappingCreationAndEditDict, IndexedRemoteField>;

const isRootObjectOption = (option: FieldMappingCreationAndEditDict): boolean => {
  const traversalPath = option.traversal_path;
  return Array.isArray(traversalPath)
    ? traversalPath.length === 0
    : traversalPath === ROOT_OBJECT_TRAVERSAL_PATH || traversalPath === "";
};

const indexedSampleValue = (option: FieldMappingCreationAndEditDict): string => {
  if (isRootObjectOption(option)) {
    return "";
  }
  const stringified = stringifyRemoteFieldValue(option.value);
  const withoutRedactions = stringified.split(REDACTED_VALUE_TEXT).join(" ");
  return withoutRedactions.slice(0, MAX_INDEXED_VALUE_LENGTH).toLowerCase();
};

export const buildRemoteFieldSearchIndex = (
  options: FieldMappingCreationAndEditDict[],
): RemoteFieldSearchIndex => {
  const index: RemoteFieldSearchIndex = new Map();
  options.forEach((option) => {
    index.set(option, {
      displayName: (option.display_name ?? "").toLowerCase(),
      commonModelName: (option.commonModelName ?? "").toLowerCase(),
      sampleValue: indexedSampleValue(option),
    });
  });
  return index;
};

export const MIN_VALUE_QUERY_LENGTH = 2;
export const MIN_COMMON_MODEL_QUERY_LENGTH = 3;

export const filterRemoteFieldOptions = (
  options: FieldMappingCreationAndEditDict[],
  query: string,
  index: RemoteFieldSearchIndex,
  matchCommonModel = false,
): FieldMappingCreationAndEditDict[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return options;
  }

  const displayNameMatches: FieldMappingCreationAndEditDict[] = [];
  const otherMatches: FieldMappingCreationAndEditDict[] = [];

  options.forEach((option) => {
    const indexed = index.get(option);
    if (!indexed) {
      return;
    }
    if (indexed.displayName.includes(normalizedQuery)) {
      displayNameMatches.push(option);
    } else if (
      (normalizedQuery.length >= MIN_VALUE_QUERY_LENGTH &&
        indexed.sampleValue.includes(normalizedQuery)) ||
      (matchCommonModel &&
        normalizedQuery.length >= MIN_COMMON_MODEL_QUERY_LENGTH &&
        indexed.commonModelName.startsWith(normalizedQuery))
    ) {
      otherMatches.push(option);
    }
  });

  return displayNameMatches.concat(otherMatches);
};

export const useRemoteFieldOptionFilter = (
  options: FieldMappingCreationAndEditDict[],
  matchCommonModel = false,
) => {
  const cache = useRef<{
    options: FieldMappingCreationAndEditDict[];
    index: RemoteFieldSearchIndex;
  } | null>(null);

  return useCallback(
    (query: string): FieldMappingCreationAndEditDict[] => {
      if (!query.trim()) {
        return options;
      }
      if (cache.current?.options !== options) {
        cache.current = { options, index: buildRemoteFieldSearchIndex(options) };
      }
      return filterRemoteFieldOptions(options, query, cache.current.index, matchCommonModel);
    },
    [options, matchCommonModel],
  );
};
